import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography, Button } from '@mui/joy';

import { FitMode } from '../VideoScaler';

interface VideoScreenFitDropdownProps {
  value: FitMode;
  onChange: (value: FitMode) => void;
  autoPlayout: boolean;
  onAutoPlayoutChange: (value: boolean) => void;
  strictFrames: boolean;
  onStrictFramesChange: (value: boolean) => void;
}

interface SettingSelectProps<T> {
  title: string;
  ariaLabel: string;
  options: { val: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}

const SettingSelect = <T extends string>({
  title,
  ariaLabel,
  options,
  value,
  onChange
}: SettingSelectProps<T>): React.JSX.Element => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.val === value) || options[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <Box className='flex items-center w-full p-2'>
      <Box className='mr-4'>
        <Typography
          level='title-sm'
          sx={{
            color: 'neutral.50',
            whiteSpace: 'nowrap'
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box className='w-full' ref={dropdownRef} sx={{ position: 'relative' }}>
        <Button
          variant='plain'
          aria-expanded={isDropdownOpen}
          aria-haspopup='listbox'
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(!isDropdownOpen);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              setIsDropdownOpen(!isDropdownOpen);
            } else if (e.key === 'Escape') {
              setIsDropdownOpen(false);
            }
          }}
          sx={{
            width: '100%',
            border: '1px solid',
            borderColor: 'neutral.50',
            backgroundColor: 'transparent',
            borderRadius: '0px',
            color: 'neutral.50',
            padding: '4px 8px',
            paddingRight: '24px',
            textAlign: 'left',
            justifyContent: 'flex-start',
            fontSize: '14px',
            lineHeight: '150%',
            fontWeight: 400,
            position: 'relative',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          {selectedOption.label}
          <Box
            component='span'
            sx={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              fontSize: '10px'
            }}
          >
            ▼
          </Box>
        </Button>
        {isDropdownOpen && (
          <Box
            role='listbox'
            aria-label={ariaLabel}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              bgColor: '#626271',
              width: '50%',
              border: '1px solid',
              borderColor: 'neutral.50',
              borderRadius: '0px',
              mt: 0.5,
              zIndex: 1001,
              boxShadow: 'none'
            }}
          >
            {options.map((item, index) => {
              const isSelected = value === item.val;
              return (
                <Box
                  key={item.val}
                  component='div'
                  role='option'
                  aria-selected={isSelected}
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(item.val);
                    setIsDropdownOpen(false);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange(item.val);
                      setIsDropdownOpen(false);
                    } else if (e.key === 'Escape') {
                      setIsDropdownOpen(false);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const nextIndex = (index + 1) % options.length;
                      const nextOption = options[nextIndex];
                      onChange(nextOption.val);
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const prevIndex = (index - 1 + options.length) % options.length;
                      const prevOption = options[prevIndex];
                      onChange(prevOption.val);
                    }
                  }}
                  sx={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    color: isSelected ? 'common.black' : 'neutral.50',
                    bgcolor: isSelected ? 'primary.bbYellow' : '#626271',
                    transition: 'background-color 0.2s, color 0.2s',
                    outline: 'none',
                    '&:hover': {
                      bgcolor: 'primary.bbYellow',
                      color: 'common.black'
                    },
                    '&:focus': {
                      bgcolor: 'primary.bbYellow',
                      color: 'common.black'
                    }
                  }}
                >
                  <Typography level='body-xs' sx={{ color: 'currentColor', lineHeight: '150%', fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const VideoScreenFitDropdown: React.FC<VideoScreenFitDropdownProps> = ({
  value,
  onChange,
  autoPlayout,
  onAutoPlayoutChange,
  strictFrames,
  onStrictFramesChange
}) => {
  const { t } = useTranslation();
  const [selectedButton, setSelectedButton] = useState<null | 'camera'>(null);

  const options = [
    { val: 'fitWidth' as FitMode, label: t('crew_view.video_screen_fit.fit_width', 'Fit Width') },
    { val: 'fitHeight' as FitMode, label: t('crew_view.video_screen_fit.fit_height', 'Fit Height') },
    { val: 'stretch' as FitMode, label: t('crew_view.video_screen_fit.stretch', 'Stretch') }
  ];

  const playoutOptions = [
    { val: 'auto', label: t('crew_view.video_playout.auto', 'Auto') },
    { val: 'actual', label: t('crew_view.video_playout.actual', 'Actual') }
  ];

  const frameFilterOptions = [
    { val: 'strict', label: t('crew_view.video_frame_filter.strict', 'Strict') },
    { val: 'off', label: t('crew_view.video_frame_filter.off', 'Off') }
  ];

  return (
    <Box
      className='py-3 px-1.5 z-40 pointer-events-auto'
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        bgcolor: 'common.black',
        border: '1px solid',
        borderColor: 'neutral.outlinedBorder',
        borderRadius: '8px',
        minWidth: '362px',
        boxShadow: 'md'
      }}
    >
      <Box className='flex flex-col items-center justify-between'>
        <Box className='flex flex-row w-full gap-2 h-15'>
          <Box className='flex items-center w-16 justify-center'>
            <Typography level='body-xs' className='w-10' sx={{ color: 'neutral.50', lineHeight: '150%' }}>
              {t('crew_view.gimbal_controls', 'Gimbal Controls')}
            </Typography>
          </Box>
          <Box className='border-l border w-0 mr-2.5' sx={{ borderColor: 'neutral.700' }} />
          <Box>
            <Button
              className='w-12 h-12'
              onClick={() => setSelectedButton(selectedButton === 'camera' ? null : 'camera')}
              sx={{
                bgcolor: selectedButton === 'camera' ? 'primary.bbYellow' : '#626271',
                color: selectedButton === 'camera' ? 'common.black' : 'neutral.50',
                '&:hover': {
                  bgcolor: 'primary.bbYellow',
                  color: 'common.black'
                },
                '&:active': {
                  bgcolor: 'primary.bbYellow',
                  color: 'common.black'
                }
              }}
            >
              <Typography
                level='body-xs'
                className='w-10'
                sx={{ color: 'currentColor', lineHeight: '150%', fontWeight: 600 }}
              >
                {t('crew_view.camera_settings', 'Camera Settings')}
              </Typography>
            </Button>
          </Box>
        </Box>
        {selectedButton === 'camera' && (
          <>
            <SettingSelect
              title={t('crew_view.video_screen_fit.title', 'Video Screen Fit')}
              ariaLabel={t('crew_view.video_screen_fit.options_aria_label', 'Video Screen Fit options')}
              options={options}
              value={value}
              onChange={onChange}
            />
            <SettingSelect
              title={t('crew_view.video_playout.title', 'Video Playback')}
              ariaLabel={t('crew_view.video_playout.options_aria_label', 'Video Playback options')}
              options={playoutOptions}
              value={autoPlayout ? 'auto' : 'actual'}
              onChange={(val) => onAutoPlayoutChange(val === 'auto')}
            />
            <SettingSelect
              title={t('crew_view.video_frame_filter.title', 'Frame Filter')}
              ariaLabel={t('crew_view.video_frame_filter.options_aria_label', 'Frame Filter options')}
              options={frameFilterOptions}
              value={strictFrames ? 'strict' : 'off'}
              onChange={(val) => onStrictFramesChange(val === 'strict')}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default VideoScreenFitDropdown;
